import React, { FormEvent, useCallback, useRef } from "react";

import { FormBuilderProps } from "./FormBuilder.types";
import { FormControl } from "./FormControl";
import { useFormBuilder } from "./useFormBuilder";
import { Button, Form } from "../../common";

export function FormBuilder<T>({
  classNames,
  submitText = "Submit",
  submitControl,
  submitFormOnEnter = false,
  onSubmit,
  onError,
  form,
  resetFormOnSubmit,
  isSubmitting,
}: FormBuilderProps<T>) {
  const { onFormSubmit, submit, formState, onValueChange, isLoading } = useFormBuilder({
    form,
    onSubmit,
    resetFormOnSubmit,
    submitFormOnEnter,
    onError,
  });

  const inputRefs = useRef(
    Object.keys(form).reduce((acc: any, key: any) => {
      acc[key] = React.createRef();
      return acc;
    }, {})
  );

  const focusNextInput = useCallback(
    (currentKey: string) => {
      const allKeys = Object.keys(form);
      const currentIndex = allKeys.indexOf(currentKey);

      const nextIndex = currentIndex + 1;

      if (nextIndex < allKeys.length) {
        const nextKey = allKeys[nextIndex];

        setTimeout(() => {
          if (inputRefs.current[nextKey] && inputRefs.current[nextKey].current) {
            inputRefs.current[nextKey]?.current.focus();
          }
        }, 100);
      } else {
        // Submit the form if there's no next input
        submitFormOnEnter && submit();
      }
    },
    [form, submitFormOnEnter, submit]
  );

  const onControlValueChange = React.useCallback(
    (key: keyof T, value: any) => {
      onValueChange(key, value);
    },
    [onValueChange]
  );

  const renderControl = (key: keyof T) => {
    const control = form[key];
    const controlState = formState[key];

    if (!controlState || (!!control.visibleIf && !control.visibleIf(formState))) {
      return null;
    }

    return (
      <FormControl
        key={String(key)}
        ref={inputRefs.current[key as string]}
        isTouched={controlState.isTouched}
        isSubmitted={controlState.isSubmitted}
        control={control}
        value={controlState.value}
        error={controlState.error}
        formState={formState}
        label={control.label}
        onChange={(value) => onControlValueChange(key, value)}
        onSubmitEditing={() => focusNextInput(key as string)}
      />
    );
  };

  const renderControls = () => Object.keys(form).map((key) => renderControl(key as keyof T));

  return (
    <Form classNames={classNames || ["formGroup"]}>
      {renderControls()}
      {submitControl ? (
        submitControl(submit)
      ) : (
        <Button
          title={submitText}
          type={"button"}
          isLoading={isLoading || isSubmitting}
          classNames={["btnPrimary", "mt-{12}", "btnBlock"]}
          onPress={(e) => {
            e.preventDefault();
            if (isLoading) return;
            submit();
          }}
        />
      )}
    </Form>
  );
}
